import { useState, useEffect } from 'react';
import { PayerDriftData, generatePayerDriftRadar, simulatePayerUpdate } from '@/lib/payerSimulation';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ShieldCheck, Zap, AlertTriangle, Activity, RefreshCw } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PayerDriftRadarLiveProps {
  onPayerSelect?: (payer: PayerDriftData) => void;
}

export function PayerDriftRadarLive({ onPayerSelect }: PayerDriftRadarLiveProps) {
  const [driftData, setDriftData] = useState<PayerDriftData[]>(() => generatePayerDriftRadar());
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isUpdating, setIsUpdating] = useState(false);

  // Live updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsUpdating(true);
      setTimeout(() => {
        setDriftData((prev) => simulatePayerUpdate(prev));
        setLastUpdate(new Date());
        setIsUpdating(false);
      }, 500);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const getDriftScoreColor = (score: number) => {
    if (score >= 75) return 'text-destructive';
    if (score >= 50) return 'text-warning';
    return 'text-success';
  };

  const getDriftScoreBg = (score: number) => {
    if (score >= 75) return 'bg-destructive/10';
    if (score >= 50) return 'bg-warning/10';
    return 'bg-success/10';
  };

  return (
    <div className="glass-card-elevated p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Payer Drift Radar</h3>
          <Badge variant="outline" className="text-xs gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
            LIVE
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className={cn("w-3 h-3", isUpdating && "animate-spin")} />
          <span>Updated {lastUpdate.toLocaleTimeString()}</span>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-xs">Payer</TableHead>
            <TableHead className="text-xs text-center">Drift Score</TableHead>
            <TableHead className="text-xs">Top Change</TableHead>
            <TableHead className="text-xs text-right">Impact</TableHead>
            <TableHead className="text-xs">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {driftData.map((payer) => (
            <TooltipProvider key={payer.payer}>
              <TableRow 
                className="cursor-pointer hover:bg-secondary/50"
                onClick={() => onPayerSelect?.(payer)}
              >
                <TableCell className="font-medium text-sm">
                  <div className="flex items-center gap-2">
                    {payer.isHighVolatility ? (
                      <AlertTriangle className="w-4 h-4 text-destructive" />
                    ) : payer.driftScore >= 50 ? (
                      <Zap className="w-4 h-4 text-warning" />
                    ) : (
                      <ShieldCheck className="w-4 h-4 text-success" />
                    )}
                    {payer.payer}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={cn(
                        "inline-flex items-center justify-center w-12 h-8 rounded font-mono font-bold text-sm",
                        getDriftScoreBg(payer.driftScore),
                        getDriftScoreColor(payer.driftScore)
                      )}>
                        {payer.driftScore}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        {payer.isHighVolatility 
                          ? 'High Volatility: Frequent rule changes detected' 
                          : payer.driftScore >= 50 
                            ? 'Moderate Drift: Monitor for changes'
                            : 'Stable: Low risk of unexpected denials'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                  {payer.topChange}
                </TableCell>
                <TableCell className="text-right">
                  <span className={cn(
                    "font-mono font-medium text-sm",
                    payer.impactAmount > 25000 ? "text-destructive" : "text-warning"
                  )}>
                    ${payer.impactAmount.toLocaleString()}
                  </span>
                </TableCell>
                <TableCell>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge 
                        className={cn(
                          "text-xs",
                          payer.isHighVolatility 
                            ? "bg-destructive/20 text-destructive border-destructive/30"
                            : "bg-success/20 text-success border-success/30"
                        )}
                      >
                        {payer.isHighVolatility ? 'High Volatility' : 'Stable'}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[300px]">
                      <p className="text-xs font-medium mb-1">AI Agent Response:</p>
                      <p className="text-xs text-muted-foreground">{payer.suggestedFix}</p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TooltipProvider>
          ))}
        </TableBody>
      </Table>

      {/* Rule Change Alerts */}
      <div className="mt-4 pt-4 border-t border-border/50">
        <h4 className="text-xs font-medium text-muted-foreground mb-3">Latest Rule Change Alerts</h4>
        <div className="space-y-2">
          {driftData
            .filter(p => p.isHighVolatility)
            .map((payer) => (
              <div 
                key={payer.payer}
                className="p-3 rounded-lg bg-destructive/5 border border-destructive/20 text-sm"
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                  <p className="text-muted-foreground">
                    <span className="font-medium text-foreground">{payer.payer}:</span>{' '}
                    {payer.ruleChangeAlert}
                  </p>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
